import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import { OpenLikeStatuses } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { PickSession } from "@redotech/redo-model/wms/schema/pick-session";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { PickSessionContext } from "../contexts/pick-session-context";

type LeaveSingleItemPickSessionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  pickSession: PickSession;
};

export const LeaveSingleItemPickSessionModal = memo(
  function LeaveSingleItemPickSessionModal({
    isOpen,
    onClose,
    onContinue,
    pickSession,
  }: LeaveSingleItemPickSessionModalProps) {
    const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
    const { fulfillmentGroups } = useRequiredContext(PickSessionContext);

    // Count remaining open orders (only those in open-like status)
    const remainingOrderCount = useMemo(() => {
      const fulfillmentGroupIds = Array.from(
        new Set(pickSession.items.map((item) => item.fulfillmentGroupId)),
      );
      return fulfillmentGroupIds.filter((groupId) => {
        const group = fulfillmentGroups[groupId];
        return group && OpenLikeStatuses.includes(group.status);
      }).length;
    }, [pickSession.items, fulfillmentGroups]);

    const [releaseLoad, triggerRelease] = useTriggerLoad(async () => {
      try {
        // Release all remaining open fulfillment groups
        const fulfillmentGroupIds = Array.from(
          new Set(pickSession.items.map((item) => item.fulfillmentGroupId)),
        ).filter((groupId) => {
          const group = fulfillmentGroups[groupId];
          return group && OpenLikeStatuses.includes(group.status);
        });

        for (const fulfillmentGroupId of fulfillmentGroupIds) {
          const result = await rpcClient.cancelPicking({ fulfillmentGroupId });
          if (!result.success) {
            toast(result.message || "Failed to release order", {
              variant: "error",
            });
            return;
          }
        }

        toast("Remaining orders released to be repicked", {
          variant: "success",
        });
        onContinue();
      } catch (error) {
        toast(`Failed to release orders: ${(error as Error).message}`, {
          variant: "error",
        });
      }
    });

    const [markProblemLoad, triggerMarkProblem] = useTriggerLoad(async () => {
      try {
        // Mark all remaining open fulfillment groups as problem
        const fulfillmentGroupIds = Array.from(
          new Set(pickSession.items.map((item) => item.fulfillmentGroupId)),
        ).filter((groupId) => {
          const group = fulfillmentGroups[groupId];
          return group && OpenLikeStatuses.includes(group.status);
        });

        for (const fulfillmentGroupId of fulfillmentGroupIds) {
          await rpcClient.markFulfillmentGroupAsProblem({
            pickSessionId: pickSession._id,
            fulfillmentGroupId,
            pickSessionItemStatuses: undefined,
          });
        }

        toast("Remaining orders marked as problem", { variant: "success" });
        onContinue();
      } catch (error) {
        toast(`Failed to mark orders as problem: ${(error as Error).message}`, {
          variant: "error",
        });
      }
    });

    const isPending = releaseLoad.pending || markProblemLoad.pending;

    return (
      <RedoModal
        hideFooter
        isOpen={isOpen}
        onModalCloseRequested={onClose}
        title="Leave pick session?"
      >
        <Flex dir="column" gap="xl" pb="xl">
          <Flex align="center" gap="md">
            <RedoIcon color="warning" Icon={AlertTriangleIcon} size={24} />
            <Text fontSize="sm" textColor="secondary">
              You have {remainingOrderCount} order
              {remainingOrderCount === 1 ? "" : "s"} remaining in this pick
              session. What would you like to do with them?
            </Text>
          </Flex>

          <Flex dir="column" gap="md" w="full">
            <RedoButton
              disabled={isPending}
              hierarchy="secondary"
              onClick={() => triggerRelease()}
              pending={releaseLoad.pending}
              size="md"
              text="Release orders to be repicked"
            />
            <RedoButton
              disabled={isPending}
              hierarchy="secondary"
              onClick={() => triggerMarkProblem()}
              pending={markProblemLoad.pending}
              size="md"
              text="Mark all as problem"
            />
            <RedoButton
              disabled={isPending}
              hierarchy="tertiary"
              onClick={onContinue}
              size="md"
              text="Continue without doing anything"
            />
          </Flex>
        </Flex>
      </RedoModal>
    );
  },
);
